import { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Loader2, Plus, Sparkles, Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { recordPendingCheckout } from '../../lib/pendingCheckout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal that presents the available pursuit slot top-up packages
 * and starts a Stripe Checkout session for the selected one. The
 * frontend never sends the price — only the package id — so users
 * can't manipulate amounts. Server side resolves to a fixed package.
 */
export default function SlotTopUpModal({ open, onOpenChange }) {
  const { authToken, user } = useAuth();
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isStarting, setIsStarting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setIsLoading(true);
    fetch(`${API_URL}/api/billing/plans`)
      .then((r) => r.json())
      .then((data) => {
        setPackages(data.slot_packages || []);
        // Default to the popular middle package
        const middle = data.slot_packages?.[1] || data.slot_packages?.[0];
        if (middle) setSelected(middle.id);
      })
      .catch((e) => console.error('Failed to load slot packages:', e))
      .finally(() => setIsLoading(false));
  }, [open]);

  const startCheckout = async () => {
    if (!selected) return;
    setIsStarting(true);
    try {
      const res = await fetch(`${API_URL}/api/billing/slots/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify({
          package_id: selected,
          // Include workspace slug so Stripe's success/cancel URLs land on
          // the tenant's workspace-scoped routes (which always exist).
          // Top-level routes also exist as a safety net.
          origin_url: `${window.location.origin}${user?.workspaceId ? `/${user.workspaceId}` : ''}`,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Could not start checkout: ${err.detail || res.statusText}`);
        return;
      }
      const data = await res.json();
      // Record the pending checkout so the dashboard can toast its outcome
      // if the user closes the return page mid-poll. `session_id` arrives in
      // the URL query the backend builds; the API also returns the raw id
      // alongside the URL.
      if (data.session_id) {
        recordPendingCheckout('SLOT_TOPUP', data.session_id);
      }
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (e) {
      console.error('Checkout error:', e);
      alert('Could not start checkout');
    } finally {
      setIsStarting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="slot-topup-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-teal-600" />
            Buy More Pursuit Slots
          </DialogTitle>
          <DialogDescription>
            Top-up packages add to your existing slot capacity immediately after payment.
            One-time charge — no subscription change.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 py-4">
            {packages.map((pkg) => {
              const isSelected = selected === pkg.id;
              return (
                <button
                  key={pkg.id}
                  onClick={() => setSelected(pkg.id)}
                  className={cn(
                    'relative p-5 rounded-lg border-2 text-left transition-all',
                    isSelected
                      ? 'border-teal-600 bg-teal-50 shadow-sm'
                      : 'border-slate-200 hover:border-slate-300 bg-white',
                  )}
                  data-testid={`slot-pkg-${pkg.id}`}
                >
                  {isSelected && (
                    <div className="absolute top-3 right-3 w-6 h-6 bg-teal-600 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                  <p className="text-3xl font-bold text-slate-900 mb-1"
                     style={{ fontFamily: 'Manrope, sans-serif' }}>
                    {pkg.quantity}
                  </p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">slots</p>
                  <p className="text-2xl font-semibold text-teal-700 mb-1">
                    ${pkg.amount.toFixed(0)}
                  </p>
                  <p className="text-xs text-slate-600 leading-relaxed">{pkg.description}</p>
                </button>
              );
            })}
          </div>
        )}

        <DialogFooter className="border-t border-slate-100 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isStarting}>
            Cancel
          </Button>
          <Button
            onClick={startCheckout}
            disabled={!selected || isStarting || isLoading}
            className="bg-teal-700 hover:bg-teal-800 text-white"
            data-testid="start-slot-checkout-btn"
          >
            {isStarting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting...</>
            ) : (
              <><Plus className="w-4 h-4 mr-2" /> Buy with Stripe</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
